import React, { useState } from 'react';
import { formatShortTimeDuration } from '../../utils/timeFormat';
import Tooltip from '../Tooltip';
import './ReferralSourcesTable.css';

const ReferralSourcesTable = ({ sources, loading }) => {
  const [sortField, setSortField] = useState('pageviews');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  if (loading) {
    return (
      <div className="referral-sources-table">
        <div className="table-header">
          <h3>Traffic Sources</h3>
        </div>
        <div className="loading-state">
          <p>Loading referral data...</p>
        </div>
      </div>
    );
  }

  if (!sources || sources.length === 0) {
    return (
      <div className="referral-sources-table">
        <div className="table-header">
          <h3>Traffic Sources</h3>
        </div>
        <div className="no-table-data">
          <p>No referral data available</p>
        </div>
      </div>
    );
  }

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    return num.toLocaleString();
  };

  const formatPercent = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) return '—';
    return `${(value * 100).toFixed(decimals)}%`;
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '—';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getEngagementColor = (rate) => {
    if (rate >= 70) return 'high';
    if (rate >= 40) return 'medium';
    return 'low';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon inactive">
          <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon active">
        <path d="M8 15l4-4 4 4" />
        <path d="M16 11H8" />
      </svg>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="sort-icon active">
        <path d="M8 9l4 4 4-4" />
        <path d="M16 13H8" />
      </svg>
    );
  };

  // Sort sources based on current sort field and direction
  const sortedSources = [...sources].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'pageviews':
        aValue = a.pageviews || 0;
        bValue = b.pageviews || 0;
        break;
      case 'sessions':
        aValue = a.sessions || 0;
        bValue = b.sessions || 0;
        break;
      case 'users':
        aValue = a.users || 0;
        bValue = b.users || 0;
        break;
      case 'engagementRate':
        aValue = a.engagementRate || 0;
        bValue = b.engagementRate || 0;
        break;
      case 'avgEngagement':
        aValue = a.avgEngagementSeconds || 0;
        bValue = b.avgEngagementSeconds || 0;
        break;
      default:
        return 0;
    }
    
    if (sortDirection === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedSources.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSources = sortedSources.slice(startIndex, endIndex);
  const totalItems = sortedSources.length;

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="referral-sources-table">
      <div className="table-header">
        <h3>Traffic Sources</h3>
        <span className="page-count">
          Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} sources
          {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </span>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th className="col-source">Source</th>
              <th className="col-number sortable" onClick={() => handleSort('pageviews')}>
                <div className="header-with-tooltip">
                  Pageviews
                  <Tooltip content={`Click to sort by pageviews ${sortField === 'pageviews' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('pageviews')}
                  </Tooltip>
                </div>
              </th>
              <th className="col-number sortable" onClick={() => handleSort('sessions')}>
                <div className="header-with-tooltip">
                  Sessions
                  <Tooltip content={`Click to sort by sessions ${sortField === 'sessions' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('sessions')}
                  </Tooltip>
                </div>
              </th>
              <th className="col-number sortable" onClick={() => handleSort('users')}>
                <div className="header-with-tooltip">
                  Users
                  <Tooltip content={`Click to sort by users ${sortField === 'users' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('users')}
                  </Tooltip>
                </div>
              </th>
              <th className="col-number sortable" onClick={() => handleSort('engagementRate')}>
                <div className="header-with-tooltip">
                  Engagement
                  <Tooltip content="Percentage of sessions where users were engaged. Higher engagement indicates better content quality and user experience.">
                    <div className="tooltip-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" />
                      </svg>
                    </div>
                  </Tooltip>
                  <Tooltip content={`Click to sort by engagement rate ${sortField === 'engagementRate' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('engagementRate')}
                  </Tooltip>
                </div>
              </th>
              <th className="col-number sortable" onClick={() => handleSort('avgEngagement')}>
                <div className="header-with-tooltip">
                  Avg. Time
                  <Tooltip content="Average time users spend engaged with content from this source. Longer times suggest more valuable or engaging content.">
                    <div className="tooltip-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4M12 8h.01" />
                      </svg>
                    </div>
                  </Tooltip>
                  <Tooltip content={`Click to sort by average time ${sortField === 'avgEngagement' ? (sortDirection === 'asc' ? '(ascending)' : '(descending)') : ''}`}>
                    {getSortIcon('avgEngagement')}
                  </Tooltip>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedSources.map((source, index) => {
              const engagementRate = source.engagementRate || 0;
              const sessions = source.sessions || 0;

              return (
                <tr key={source.source || startIndex + index}>
                  <td className="col-source">
                    <div className="source-info">
                      <span className="source-name">{source.source || '(direct)'}</span>
                      {source.medium && (
                        <span className="source-medium">via {source.medium}</span>
                      )}
                    </div>
                  </td>
                  <td className="col-number">
                    <strong>{formatNumber(source.pageviews)}</strong>
                  </td>
                  <td className="col-number">
                    {formatNumber(sessions)}
                  </td>
                  <td className="col-number">
                    {formatNumber(source.users)}
                  </td>
                  <td className="col-number">
                    <Tooltip content={`${engagementRate.toFixed(1)}% of sessions from this source were engaged. This means ${Math.round((engagementRate / 100) * sessions)} out of ${sessions} sessions lasted longer than 10 seconds, had 2+ pageviews, or included a conversion.`}>
                      <span className={`engagement-badge ${getEngagementColor(engagementRate)}`}>
                        {formatPercent(engagementRate, 0)}
                      </span>
                    </Tooltip>
                  </td>
                  <td className="col-number">
                    <Tooltip content={`Users from this source spend an average of ${formatShortTimeDuration(source.avgEngagementSeconds || 0)} engaged with the page content.`}>
                      <span className="avg-time-value">
                        {formatDuration(source.avgEngagementSeconds || 0)}
                      </span>
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '20px', 
          padding: '15px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage === 1 ? '#f0f0f0' : '#007bff',
              color: currentPage === 1 ? '#999' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            ← Previous
          </button>
          <span style={{ fontSize: '14px', color: '#666' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              backgroundColor: currentPage === totalPages ? '#f0f0f0' : '#007bff',
              color: currentPage === totalPages ? '#999' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default ReferralSourcesTable;

